export const CERTIFICATE_FORMATS = [
    { key: 'pdf', label: 'PDF', extension: 'pdf' },
    { key: 'png', label: 'PNG', extension: 'png' },
    { key: 'jpg', label: 'JPG', extension: 'jpg' },
];

const JPEG_QUALITY = 0.95;
const IMAGE_PDF_LONG_SIDE_IN = 11.69;
const PAGE_HEIGHT_TOLERANCE = 1.002;

function saveBlob(blob, filename) {
    const blobUrl = window.URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(blobUrl);
}

function canvasToBlob(canvas, type, quality) {
    return new Promise((resolve, reject) => {
        canvas.toBlob((blob) => {
            if (blob) {
                resolve(blob);
            } else {
                reject(new Error('No se pudo generar la imagen del certificado.'));
            }
        }, type, quality);
    });
}

function blobToImage(blob) {
    return new Promise((resolve, reject) => {
        const objectUrl = window.URL.createObjectURL(blob);
        const image = new Image();

        image.onload = () => {
            window.URL.revokeObjectURL(objectUrl);
            resolve(image);
        };
        image.onerror = () => {
            window.URL.revokeObjectURL(objectUrl);
            reject(new Error('No se pudo leer la imagen del certificado.'));
        };
        image.src = objectUrl;
    });
}

async function blobToCanvas(blob) {
    const image = await blobToImage(blob);

    const canvas = document.createElement('canvas');
    canvas.width = image.naturalWidth;
    canvas.height = image.naturalHeight;

    const context = canvas.getContext('2d');
    context.fillStyle = '#ffffff';
    context.fillRect(0, 0, canvas.width, canvas.height);
    context.drawImage(image, 0, 0);

    return canvas;
}

async function canvasToPdf(canvas, filename) {
    const html2pdf = (await import('html2pdf.js')).default;

    const isPortrait = canvas.height > canvas.width;
    const pageWidthIn = isPortrait
        ? IMAGE_PDF_LONG_SIDE_IN * (canvas.width / canvas.height)
        : IMAGE_PDF_LONG_SIDE_IN;
    const pageHeightIn = pageWidthIn * (canvas.height / canvas.width) * PAGE_HEIGHT_TOLERANCE;

    await html2pdf()
        .from(canvas, 'canvas')
        .set({
            filename,
            image: { type: 'jpeg', quality: 0.98 },
            jsPDF: {
                unit: 'in',
                format: [pageWidthIn, pageHeightIn],
                orientation: isPortrait ? 'portrait' : 'landscape',
            },
        })
        .save();
}

//Certificado que ya viene como imagen (plantilla propia / Canva)

async function exportImageCertificate(imageUrl, format, baseName) {
    const response = await fetch(imageUrl);

    if (!response.ok) {
        throw new Error('No se pudo descargar la imagen del certificado.');
    }

    const blob = await response.blob();

    if (format === 'png') {
        saveBlob(blob, `${baseName}.png`);
        return;
    }

    const canvas = await blobToCanvas(blob);

    if (format === 'jpg') {
        saveBlob(await canvasToBlob(canvas, 'image/jpeg', JPEG_QUALITY), `${baseName}.jpg`);
        return;
    }

    await canvasToPdf(canvas, `${baseName}.pdf`);
}

//Certificado en HTML (plantilla predefinida)
async function withRenderedCertificate(html, callback) {
    const hideWrapper = document.createElement('div');
    hideWrapper.style.height = '0';
    hideWrapper.style.overflow = 'hidden';
    document.body.appendChild(hideWrapper);

    try {
        const container = document.createElement('div');
        container.style.width = '1400px';
        container.style.backgroundColor = '#ffffff';
        container.innerHTML = html;
        hideWrapper.appendChild(container);
        await new Promise((resolve) => setTimeout(resolve, 150));
        const certificateRoot = container.firstElementChild;
        const isFixedSize = Boolean(certificateRoot) && certificateRoot.offsetWidth < container.offsetWidth - 2;

        let captureWidth = 1400;
        let captureScale;
        let pageWidthIn = 14;

        if (isFixedSize) {
            captureWidth = certificateRoot.offsetWidth;
            captureScale = 2;
            pageWidthIn = captureWidth / 100;
            container.style.width = `${captureWidth}px`;
        }

        const pageHeightIn = pageWidthIn * (container.offsetHeight / container.offsetWidth);

        return await callback({ container, captureWidth, captureScale, pageWidthIn, pageHeightIn });
    } finally {
        hideWrapper.remove();
    }
}

function html2canvasOptions({ captureWidth, captureScale }) {
    return {
        dpi: 192,
        letterRendering: true,
        useCORS: true,
        backgroundColor: '#ffffff',
        width: captureWidth,
        windowWidth: captureWidth,
        ...(captureScale ? { scale: captureScale } : {}),
    };
}

async function exportHtmlCertificate(html, format, baseName) {
    const html2pdf = (await import('html2pdf.js')).default;

    await withRenderedCertificate(html, async (layout) => {
        if (format === 'pdf') {
            await html2pdf()
                .from(layout.container)
                .set({
                    filename: `${baseName}.pdf`,
                    image: { type: 'jpeg', quality: 0.95 },
                    html2canvas: html2canvasOptions(layout),
                    jsPDF: {
                        unit: 'in',
                        format: [layout.pageWidthIn, layout.pageHeightIn],
                        orientation: layout.pageHeightIn > layout.pageWidthIn ? 'portrait' : 'landscape',
                    },
                })
                .save();

            return;
        }

        const canvas = await html2pdf()
            .from(layout.container)
            .set({
                html2canvas: html2canvasOptions({ ...layout, captureScale: layout.captureScale ?? 2 }),
            })
            .toCanvas()
            .get('canvas');

        if (format === 'jpg') {
            saveBlob(await canvasToBlob(canvas, 'image/jpeg', JPEG_QUALITY), `${baseName}.jpg`);
        } else {
            saveBlob(await canvasToBlob(canvas, 'image/png'), `${baseName}.png`);
        }
    });
}

//Descarga el certificado en el formato pedido ('pdf' | 'png' | 'jpg')
export async function downloadCertificateFile(payload, format, baseName) {
    if (!CERTIFICATE_FORMATS.some((item) => item.key === format)) {
        throw new Error(`Formato de descarga desconocido: ${format}`);
    }

    if (payload?.type === 'image') {
        return exportImageCertificate(payload.image_url, format, baseName);
    }

    if (payload?.certificate) {
        return exportHtmlCertificate(payload.certificate, format, baseName);
    }

    throw new Error('El servidor no devolvió datos del certificado.');
}
